import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement } from '~/utils/rbac'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Les trois onglets vivent sur la même route (`/sensors`), l'onglet courant est un search param. */
export type SensorsTab = 'sensors' | 'fridges' | 'settings'

export const SENSOR_READ: PermissionTuple = ['read', 'sensors.sensors.sensor']
export const SENSOR_ADD: PermissionTuple = ['add', 'sensors.sensors.sensor']
export const SENSOR_EDIT: PermissionTuple = ['edit', 'sensors.sensors.sensor']
export const SENSOR_DELETE: PermissionTuple = ['delete', 'sensors.sensors.sensor']

export const FRIDGE_READ: PermissionTuple = ['read', 'sensors.fridges.fridge']
export const FRIDGE_ADD: PermissionTuple = ['add', 'sensors.fridges.fridge']
export const FRIDGE_EDIT: PermissionTuple = ['edit', 'sensors.fridges.fridge']
export const FRIDGE_DELETE: PermissionTuple = ['delete', 'sensors.fridges.fridge']

/**
 * Onglet « Réglages » : seuils par catégorie d'équipement (AMBIANT = capteurs de salle, FRIGO et
 * CHAMBRE_FROIDE = réfrigérateurs). La taxonomie n'expose pas de ressource propre à ces réglages —
 * ils sont donc gardés par les droits des équipements auxquels ils s'appliquent, en OU. Même
 * exigence côté backend sur `GET|PATCH /sensors/alert-settings`.
 */
export const SENSORS_SETTINGS_READ: PermissionRequirement = { any: [SENSOR_READ, FRIDGE_READ] }
export const SENSORS_SETTINGS_EDIT: PermissionRequirement = { any: [SENSOR_EDIT, FRIDGE_EDIT] }

/**
 * Acquittement d'une alerte moteur (tiroir « Alertes actives »). Même absence de ressource dédiée :
 * l'alerte porte sur un équipement, d'où le droit de modification de l'un ou l'autre.
 */
export const SENSORS_ALERT_ACKNOWLEDGE: PermissionRequirement = { any: [SENSOR_EDIT, FRIDGE_EDIT] }

/** Accès à l'écran : dès qu'un seul des trois onglets est accessible. */
export const SENSORS_PAGE_READ: PermissionRequirement = { any: [SENSOR_READ, FRIDGE_READ] }

/**
 * Source unique des droits de lecture des sous-onglets Capteurs.
 *
 * Consommée par la barre d'onglets et par le `staticData.permission` de `/sensors` : un seul
 * endroit à mettre à jour, pas de dérive possible entre l'onglet affiché et son contenu.
 */
export const SENSORS_TAB_PERMISSIONS = {
  sensors: SENSOR_READ,
  fridges: FRIDGE_READ,
  settings: SENSORS_SETTINGS_READ,
} as const satisfies Record<SensorsTab, PermissionRequirement>

/** Les onglets n'ayant pas de route dédiée, chacun porte explicitement son exigence. */
type SensorsTabDefinition = GuardedTab<SensorsTab> & { permission: PermissionRequirement }

/** Ordre de déclaration = ordre d'affichage, et ordre de repli quand l'onglet actif est interdit. */
export const SENSORS_TABS: SensorsTabDefinition[] = [
  { value: 'sensors', label: 'Capteurs', permission: SENSORS_TAB_PERMISSIONS.sensors },
  { value: 'fridges', label: 'Réfrigérateurs', permission: SENSORS_TAB_PERMISSIONS.fridges },
  { value: 'settings', label: 'Réglages', permission: SENSORS_TAB_PERMISSIONS.settings },
]
